import { defineStore, storeToRefs } from "pinia";
import { computed, ref, ComputedRef } from "vue";
import { MenuItem } from "@/models/menu-item";
import axios from "axios";
import { map } from "underscore";
import { useAuthStore } from "./useAuthStore";

export interface MenuState {
  menuItems: MenuItem[];
  isMenuItemsLoading: boolean;
  isMobileMenuOpen: boolean;
  menuItemsHasError: boolean;
}

export const useMenuStore = defineStore("menu", () => {
  // STATES
  const menuItems = ref<MenuItem[]>([]);
  const isMenuItemsLoading = ref<boolean>(false);
  const isMobileMenuOpen = ref<boolean>(false);
  const menuItemsHasError = ref<boolean>(false);

  // ACTIONS
  const setMenuItemsLoading = (loading: boolean) => {
    isMenuItemsLoading.value = loading;
  };
  const loadMenuItems = async () => {
    if (menuItems.value.length) {
      return;
    }
    isMenuItemsLoading.value = true;
    const { getRequestHeader, getUserProfile } = storeToRefs(useAuthStore());

    await axios
      .get(
        `/api/framework/services?service=${getUserProfile.value.serviceType}`,
        getRequestHeader.value,
      )
      .then(({ data }) => {
        menuItems.value = data;
        menuItemsHasError.value = false;
      })
      .catch(() => {
        menuItemsHasError.value = true;
      })
      .finally(() => (isMenuItemsLoading.value = false));
  };
  const setMobileMenuDisplay = (isMenuOpen: boolean) => {
    isMobileMenuOpen.value = isMenuOpen;
  };
  const setMenuItemsHasError = (hasError: boolean) => {
    menuItemsHasError.value = hasError;
  };

  // GETTERS
  const getMenuItems: ComputedRef<MenuItem[]> = computed(() => {
    return map(menuItems.value, (menu: { name: string }) => ({
      ...menu,
      value: menu.name,
    }));
  });
  const getMenuItemsHasError: ComputedRef<boolean> = computed(
    () => menuItemsHasError.value,
  );
  const getMobileMenuOpen: ComputedRef<boolean> = computed(
    () => isMobileMenuOpen.value,
  );

  return {
    getMenuItems,
    getMenuItemsHasError,
    getMobileMenuOpen,
    setMenuItemsLoading,
    loadMenuItems,
    setMobileMenuDisplay,
    setMenuItemsHasError,
  };
});
